import {ComponentProps, forwardRef} from 'react';
import styled, {keyframes} from 'styled-components';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  min-width: 0;
  grid-area: ${(props) => (props.style?.gridArea ? props.style?.gridArea : '')};
`;

const Input = styled.input<InputProps>`
  border: 1px solid
    ${(props) =>
      props.error ? props.theme.colors.danger500 : props.theme.colors.border};
  border-radius: 14px;
  height: 46px;
  width: 100%;
  margin-top: 6px;
  padding: 5px 14px;
  font-size: 13px;
  line-height: 1.5;
  font-weight: 500;
  opacity: ${(p) => (p.disabled ? 0.5 : 1)};
  color: ${(props) => props.theme.colors.textPrimary};
  background: ${(props) =>
    props.error
      ? props.theme.colors.dangerSoft
      : props.theme.colors.neutral100};
  transition: border-color ${(p) => p.theme.motion.duration.base}
      ${(p) => p.theme.motion.easing.out},
    box-shadow ${(p) => p.theme.motion.duration.base}
      ${(p) => p.theme.motion.easing.out},
    background ${(p) => p.theme.motion.duration.base}
      ${(p) => p.theme.motion.easing.out};

  &::placeholder {
    color: ${(props) => props.theme.colors.textMuted};
    font-weight: 400;
  }

  &:hover:not(:disabled) {
    border-color: ${(props) =>
      props.error
        ? props.theme.colors.danger500
        : props.theme.colors.borderStrong};
  }

  &:focus {
    outline: none;
    border-color: ${(props) =>
      props.error
        ? props.theme.colors.danger500
        : props.theme.colors.violet600};
    box-shadow: 0 0 0 3px
      ${(props) =>
        props.error ? 'rgba(225, 1, 1, 0.14)' : 'rgba(116, 104, 214, 0.18)'};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const FieldLabel = styled.label`
  display: flex;
  align-items: baseline;
  gap: 4px;
  padding-left: 2px;
  margin: 0;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.5;
  letter-spacing: 0.1px;
  color: ${(p) => p.theme.colors.textSecondary};
`;

const errorSlideIn = keyframes`
  from { opacity: 0; transform: translate3d(0, -4px, 0); max-height: 0; }
  to { opacity: 1; transform: translate3d(0, 0, 0); max-height: 80px; }
`;

export const ErrorContainer = styled.div<ErrorProps>`
  display: ${(props) => (props.show ? 'flex' : 'none')};
  align-items: flex-start;
  gap: 6px;
  margin-top: 6px;
  padding: 0 2px;
  max-width: 100%;
  animation: ${errorSlideIn} ${(p) => p.theme.motion.duration.base}
    ${(p) => p.theme.motion.easing.out} both;

  &::before {
    content: '';
    flex-shrink: 0;
    width: 14px;
    height: 14px;
    margin-top: 2px;
    background-color: ${(p) => p.theme.colors.danger500};
    -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23000' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M12 8v5m0 3.5v.01M10.3 3.2 2.5 17a2 2 0 0 0 1.7 3h15.6a2 2 0 0 0 1.7-3L13.7 3.2a2 2 0 0 0-3.4 0Z'/%3E%3C/svg%3E")
      no-repeat center / contain;
    mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23000' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M12 8v5m0 3.5v.01M10.3 3.2 2.5 17a2 2 0 0 0 1.7 3h15.6a2 2 0 0 0 1.7-3L13.7 3.2a2 2 0 0 0-3.4 0Z'/%3E%3C/svg%3E")
      no-repeat center / contain;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const HintContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  margin-top: 5px;
`;

export const ErrorTriangle = styled.div`
  display: none;
`;

export const ErrorTriangleUpsideDown = styled.div`
  display: none;
`;

const HintText = styled.span`
  color: ${(props) => props.theme.colors.textMuted};
  font-size: 0.75rem;
`;

export const ExtraText = styled.span`
  color: ${(props) => props.theme.colors.textMuted};
  margin-left: 2px;
`;

export const ErrorText = styled.span`
  color: ${(props) => props.theme.colors.danger500};
  font-size: 12px;
  font-weight: 500;
  line-height: 1.45;
`;

interface AuthInputProps extends Omit<ComponentProps<typeof Input>, 'error'> {
  label?: string | null;
  type?: string;
  error?: string | null;
  required?: boolean;
  hint?: string | null;
  extras?: string;
  disabled?: boolean;
  noHeightErrorContainer?: boolean;
  placeHolder?: string | null;
}
export interface ErrorProps {
  show: boolean;
  noHeight?: boolean;
}
interface InputProps {
  error: boolean;
}

export const AuthInput = forwardRef<HTMLInputElement, AuthInputProps>(
  (
    {
      label,
      required,
      error,
      type,
      hint,
      extras,
      disabled = false,
      placeHolder,
      noHeightErrorContainer = false,
      ...props
    }: AuthInputProps,
    ref
  ) => {
    return (
      <Container style={{gridArea: props.style?.gridArea}}>
        {label ? (
          <FieldLabel>
            <span>
              {label}
              {required ? ' *' : ''}
            </span>
            <ExtraText>{extras}</ExtraText>
          </FieldLabel>
        ) : null}
        <Input
          disabled={disabled}
          error={error ? true : false}
          {...props}
          placeholder={placeHolder || undefined}
          type={type || 'text'}
          ref={ref}
        />
        <ErrorContainer
          noHeight={noHeightErrorContainer}
          show={error ? true : false}
        >
          {error ? (
            <>
              {/* <Image
                width={18}
                height={18}
                src="/icons/error.svg"
                alt="error icon"
              /> */}
              <ErrorText className="text-l12">{error}</ErrorText>
            </>
          ) : hint ? (
            <>
              <HintText>{hint}</HintText>
            </>
          ) : (
            ''
          )}
          <ErrorTriangle></ErrorTriangle>
        </ErrorContainer>
        <HintContainer>
          {hint ? (
            <>
              <HintText>{hint}</HintText>
            </>
          ) : (
            ''
          )}
        </HintContainer>
      </Container>
    );
  }
);
